(function () {
  const CHECKOUT_SELECTOR = '[data-scheduled-delivery-checkout]';
  const PANEL_SELECTOR = '[data-scheduled-delivery-panel]';
  const DATE_SELECTOR = '[data-scheduled-delivery-date]';
  const TIME_SELECTOR = '[data-scheduled-delivery-time]';
  const STATUS_SELECTOR = '[data-scheduled-delivery-status]';
  const MESSAGE_SELECTOR = '[data-scheduled-delivery-message]';
  const BANNER_SELECTOR = '[data-scheduled-delivery-banner]';
  const ATTR_DELIVERY_DATE = 'delivery_date';
  const ATTR_DELIVERY_TIME = 'delivery_time';
  const SLOT_HOURS = 2;
  const DAYS_TO_SHOW = 5;

  let cart = null;
  let state = {
    schedulingRequired: false,
    selectedDate: '',
    selectedTime: '',
    savedDate: '',
    savedTime: '',
    closedBrands: [],
    scheduledBrands: [],
    eligibleDates: [],
    isEditingSelection: false,
  };
  let isRendering = false;

  function parseScheduleData() {
    const source = document.querySelector('cart-drawer [data-brand-schedules-json]');
    if (!source) return [];

    try {
      const parsed = JSON.parse(source.textContent || '[]');
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch (error) {
      console.warn('Unable to parse brand schedules', error);
      return [];
    }
  }

  function getSchedules() {
    window.metaobjects = window.metaobjects || {};
    window.metaobjects.brand_schedule = window.metaobjects.brand_schedule || {};
    window.metaobjects.brand_schedule.values = parseScheduleData();

    const metaobjects = window.metaobjects;
    const schedules = metaobjects.brand_schedule.values;
    return Array.isArray(schedules) ? schedules : [];
  }

  function parseHour(timeStr) {
    return parseInt(String(timeStr || '0:00').split(':')[0], 10);
  }

  function normalizeOffDays(value) {
    if (Array.isArray(value)) {
      return value.map((item) => String(item).trim()).filter(Boolean);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return normalizeOffDays(parsed);
      } catch {
        /* String lists are handled below. */
      }

      return trimmed
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [];
  }

  function normalizeSchedule(schedule) {
    return {
      brand_name: String(schedule?.brand_name || '').trim(),
      open_time: String(schedule?.open_time || '10:00').trim(),
      close_time: String(schedule?.close_time || '18:00').trim(),
      off_days: normalizeOffDays(schedule?.off_days),
      is_closed_today: schedule?.is_closed_today === true || schedule?.is_closed_today === 'true',
    };
  }

  function getDateKey(date) {
    return date.toISOString().split('T')[0];
  }

  function getWeekday(date) {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }

  function isScheduleClosedNow(schedule, now) {
    const openHour = parseHour(schedule.open_time);
    const closeHour = parseHour(schedule.close_time);
    const currentHour = now.getHours();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
    const currentDate = now.toISOString().split('T')[0];

    return (
      schedule.is_closed_today === true ||
      schedule.off_days.includes(currentDay) ||
      schedule.off_days.includes(currentDate) ||
      currentHour < openHour ||
      currentHour >= closeHour
    );
  }

  function isScheduleOffOnDate(schedule, date) {
    return schedule.off_days.includes(getWeekday(date)) || schedule.off_days.includes(getDateKey(date));
  }

  function unique(values) {
    return Array.from(new Set(values.filter(Boolean)));
  }

  function getCartBrands(cartData) {
    return unique((cartData?.items || []).map((item) => item.vendor));
  }

  function getMatchedSchedules(cartData) {
    const schedules = getSchedules().map(normalizeSchedule).filter((schedule) => schedule.brand_name);
    const cartBrands = getCartBrands(cartData);

    return cartBrands
      .map((brand) => {
        const schedule = schedules.find((entry) => entry.brand_name === brand);
        return schedule ? { brand, schedule } : null;
      })
      .filter(Boolean);
  }

  function buildDecision(cartData) {
    const now = new Date();
    const scheduledBrands = getMatchedSchedules(cartData);
    const closedBrands = scheduledBrands.filter(({ schedule }) => isScheduleClosedNow(schedule, now));

    return {
      schedulingRequired: closedBrands.length > 0,
      closedBrands,
      scheduledBrands,
    };
  }

  function buildEligibleDates(scheduledBrands) {
    const dates = [];

    for (let offset = 1; offset <= DAYS_TO_SHOW; offset += 1) {
      const date = new Date();
      date.setDate(date.getDate() + offset);

      const isOffDay = scheduledBrands.some(({ schedule }) => isScheduleOffOnDate(schedule, date));
      if (!isOffDay) {
        dates.push({
          value: getDateKey(date),
          label: date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          }),
        });
      }
    }

    return dates;
  }

  function generateSlots(open, close) {
    const slots = [];
    let start = parseInt(String(open || '0:00').split(':')[0], 10);
    const closeHour = parseInt(String(close || '0:00').split(':')[0], 10);

    while (start < closeHour) {
      const end = start + SLOT_HOURS;

      slots.push(
        `${String(start).padStart(2, '0')}:00–${String(end).padStart(2, '0')}:00`
      );

      start = end;
    }

    return slots;
  }

  function buildTimeSlots(scheduledBrands) {
    const schedules = scheduledBrands.map(({ schedule }) => schedule);
    const openHour = Math.max(...schedules.map((schedule) => parseHour(schedule.open_time)));
    const closeHour = Math.min(...schedules.map((schedule) => parseHour(schedule.close_time)));
    const slots = [];

    if (!Number.isFinite(openHour) || !Number.isFinite(closeHour) || openHour >= closeHour) {
      return slots;
    }

    return generateSlots(`${String(openHour).padStart(2, '0')}:00`, `${String(closeHour).padStart(2, '0')}:00`);
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to fetch cart');
    return response.json();
  }

  async function saveDeliveryAttributes(date, time) {
    console.log('Saved date:', date);
    console.log('Saved time:', time);

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          [ATTR_DELIVERY_DATE]: date,
          [ATTR_DELIVERY_TIME]: time,
        },
      }),
    });

    if (!response.ok) throw new Error('Unable to save delivery schedule');
    cart = await response.json();
    console.log('Schedule saved', cart);
    state.savedDate = date;
    state.savedTime = time;
    state.isEditingSelection = false;
  }

  function getElements() {
    return {
      checkoutButton: document.querySelector(CHECKOUT_SELECTOR),
      panel: document.querySelector(PANEL_SELECTOR),
      dateSelect: document.querySelector(DATE_SELECTOR),
      timeSelect: document.querySelector(TIME_SELECTOR),
      status: document.querySelector(STATUS_SELECTOR),
      message: document.querySelector(MESSAGE_SELECTOR),
      banner: document.querySelector(BANNER_SELECTOR),
    };
  }

  function setStatus(message, isError) {
    const { status } = getElements();
    if (!status) return;
    status.textContent = message || '';
    status.classList.toggle('is-error', Boolean(isError));
  }

  function setCheckoutAsSubmit(button) {
    button.type = 'submit';
    button.name = 'checkout';
    button.setAttribute('form', 'CartDrawer-Form');
    button.disabled = false;
    button.textContent = 'Proceed to Buy';
    button.removeAttribute('aria-expanded');
  }

  function setCheckoutAsScheduler(button) {
    button.type = 'button';
    button.removeAttribute('name');
    button.removeAttribute('form');
    button.disabled = false;
    button.textContent = 'Schedule your delivery';
    button.setAttribute('aria-expanded', 'false');
  }

  function setCheckoutAsScheduledSubmit(button) {
    button.type = 'submit';
    button.name = 'checkout';
    button.setAttribute('form', 'CartDrawer-Form');
    button.disabled = false;
    button.textContent = 'Schedule your delivery';
    button.setAttribute('aria-expanded', 'true');
  }

  function clearSavedSelection() {
    state.savedDate = '';
    state.savedTime = '';
    state.isEditingSelection = true;
  }

  function populateDates() {
    const { dateSelect } = getElements();
    if (!dateSelect) return;

    dateSelect.innerHTML = '';
    state.eligibleDates.forEach((date) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cart-drawer__schedule-option';
      button.dataset.value = date.value;
      button.textContent = date.label;
      button.setAttribute('aria-pressed', String(date.value === state.selectedDate));
      dateSelect.append(button);
    });
  }

  function markSelectedOptions() {
    const { dateSelect, timeSelect } = getElements();

    dateSelect?.querySelectorAll('.cart-drawer__schedule-option').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.value === state.selectedDate));
    });

    timeSelect?.querySelectorAll('.cart-drawer__schedule-option').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.value === state.selectedTime));
    });
  }

  function populateTimes() {
    const { timeSelect } = getElements();
    if (!timeSelect) return;

    const slots = state.selectedDate ? buildTimeSlots(state.scheduledBrands) : [];
    timeSelect.innerHTML = '';

    slots.forEach((slot) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cart-drawer__schedule-option';
      button.dataset.value = slot;
      button.textContent = slot;
      button.setAttribute('aria-pressed', String(slot === state.selectedTime));
      timeSelect.append(button);
    });

    if (!slots.length) {
      const empty = document.createElement('p');
      empty.className = 'cart-drawer__schedule-empty';
      empty.textContent = state.selectedDate ? 'No slots available' : 'Pick a date first';
      timeSelect.append(empty);
    }

    if (!slots.includes(state.selectedTime)) {
      state.selectedTime = '';
    }
  }

  function showPanel() {
    const { panel, checkoutButton } = getElements();
    if (!panel) return;

    panel.hidden = false;
    if (checkoutButton) checkoutButton.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      const { dateSelect } = getElements();
      dateSelect?.querySelector('button')?.focus();
    });
  }

  function hidePanel() {
    const { panel } = getElements();
    if (panel) panel.hidden = true;
  }

  function hasSavedSelection() {
    if (!state.savedDate || !state.savedTime) return false;
    const dateIsAvailable = state.eligibleDates.some((date) => date.value === state.savedDate);
    const timeIsAvailable = buildTimeSlots(state.scheduledBrands).includes(state.savedTime);
    return dateIsAvailable && timeIsAvailable;
  }

  function render() {
    const { checkoutButton, panel, message, banner } = getElements();
    if (!checkoutButton) return;

    isRendering = true;
    try {
      if (!state.schedulingRequired) {
        setCheckoutAsSubmit(checkoutButton);
        hidePanel();
        if (banner) banner.hidden = true;
        setStatus('');
        updateClosedDeliveryHeaders();
        return;
      }

      state.eligibleDates = buildEligibleDates(state.scheduledBrands);
      const dateIsAvailable = state.eligibleDates.some((date) => date.value === state.savedDate);
      const timeIsAvailable = buildTimeSlots(state.scheduledBrands).includes(state.savedTime);
      if (state.savedDate && state.savedTime && (!dateIsAvailable || !timeIsAvailable)) {
        state.savedDate = '';
        state.savedTime = '';
        state.selectedDate = dateIsAvailable ? state.selectedDate : '';
        state.selectedTime = '';
      }
      if (!state.selectedDate && state.eligibleDates.length) {
        state.selectedDate = state.eligibleDates[0].value;
      }
      populateDates();
      populateTimes();

      if (message) {
        const brandNames = state.closedBrands.map(({ brand }) => brand).join(', ');
        message.innerHTML = `${brandNames || 'This brand'} is currently closed.<br>Go ahead and schedule your delivery to receive your favorite outfit at your perfect time ✨`;
      }
      if (banner) banner.hidden = false;

      if (!state.eligibleDates.length) {
        setStatus('No delivery slots are available right now. Please try again later.', true);
      }

      setCheckoutAsScheduler(checkoutButton);
      updateClosedDeliveryHeaders();

      if (hasSavedSelection()) {
        if (panel) panel.hidden = false;
        setCheckoutAsScheduledSubmit(checkoutButton);
        setStatus('Delivery scheduled successfully ✨');
        return;
      }
    } finally {
      isRendering = false;
    }
  }

  async function evaluate() {
    const { checkoutButton } = getElements();
    if (!checkoutButton) return;

    try {
      cart = await fetchCart();
    } catch (error) {
      console.warn('Scheduled delivery could not fetch cart', error);
      return;
    }

    const decision = buildDecision(cart);
    const attrs = cart?.attributes && typeof cart.attributes === 'object' ? cart.attributes : {};

    state = {
      ...state,
      schedulingRequired: decision.schedulingRequired,
      closedBrands: decision.closedBrands,
      scheduledBrands: decision.scheduledBrands,
      selectedDate: state.isEditingSelection ? state.selectedDate : attrs[ATTR_DELIVERY_DATE] || '',
      selectedTime: state.isEditingSelection ? state.selectedTime : attrs[ATTR_DELIVERY_TIME] || '',
      savedDate: state.isEditingSelection ? state.savedDate : attrs[ATTR_DELIVERY_DATE] || '',
      savedTime: state.isEditingSelection ? state.savedTime : attrs[ATTR_DELIVERY_TIME] || '',
    };

    render();
  }

  async function handleSelectionChange() {
    const { checkoutButton } = getElements();

    if (!state.selectedDate || !state.selectedTime) {
      setStatus('');
      return;
    }

    try {
      if (checkoutButton) checkoutButton.disabled = true;
      setStatus('Saving delivery schedule...');
      await saveDeliveryAttributes(state.selectedDate, state.selectedTime);
      setStatus('Delivery scheduled successfully ✨');
      if (checkoutButton) checkoutButton.disabled = false;
      render();
    } catch (error) {
      console.warn(error);
      setStatus("We couldn't save your delivery time. Please try again.", true);
      if (checkoutButton) checkoutButton.disabled = false;
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest(CHECKOUT_SELECTOR);
    if (!button || !state.schedulingRequired || hasSavedSelection()) return;

    event.preventDefault();
    showPanel();
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches(`${DATE_SELECTOR}, ${TIME_SELECTOR}`)) return;
    event.preventDefault();
  });

  document.addEventListener('click', (event) => {
    const optionButton = event.target.closest('.cart-drawer__schedule-option');
    if (!optionButton) return;

    const dateGroup = optionButton.closest(DATE_SELECTOR);
    if (dateGroup) {
      event.preventDefault();
      state.selectedDate = optionButton.dataset.value || '';
      state.selectedTime = '';
      clearSavedSelection();
      populateDates();
      populateTimes();
      markSelectedOptions();
      const { checkoutButton } = getElements();
      if (checkoutButton && state.schedulingRequired) {
        setCheckoutAsScheduler(checkoutButton);
      }
      setStatus(state.selectedDate ? 'Choose a time slot to save your delivery.' : '');
      return;
    }

    const timeGroup = optionButton.closest(TIME_SELECTOR);
    if (timeGroup) {
      event.preventDefault();
      state.selectedTime = optionButton.dataset.value || '';
      clearSavedSelection();
      markSelectedOptions();
      handleSelectionChange();
    }
  });

  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      if (!submitter || !submitter.matches(CHECKOUT_SELECTOR)) return;
      if (!state.schedulingRequired || hasSavedSelection()) return;

      event.preventDefault();
      showPanel();
    },
    true
  );

  document.addEventListener('cart:updated', () => {
    window.setTimeout(evaluate, 50);
  });

  document.addEventListener('DOMContentLoaded', evaluate);
  window.addEventListener('pageshow', evaluate);

  function updateClosedDeliveryHeaders() {
    const closedBrandNames = state.closedBrands.map(({ brand }) => String(brand || '').trim()).filter(Boolean);
    document.querySelectorAll('cart-drawer tbody.delivery-group.is-closed-store').forEach((group) => {
      group.classList.remove('is-closed-store');
    });
    if (!closedBrandNames.length) return;

    document.querySelectorAll('cart-drawer tbody.delivery-group').forEach((group) => {
      const groupVendors = Array.from(group.querySelectorAll('.cart-item[data-vendor]')).map((row) =>
        String(row.getAttribute('data-vendor') || '').trim()
      );
      const closedVendor = closedBrandNames.find((brand) => groupVendors.includes(brand));
      if (!closedVendor) return;

      group.classList.add('is-closed-store');

      const icon = group.querySelector('.delivery-group__icon');
      if (icon) icon.textContent = '⏱';

      const eta = group.querySelector('.delivery-group__eta');
      if (eta) eta.textContent = `${closedVendor} is closed`;

      const vendors = group.querySelector('.delivery-group__vendors');
      if (vendors) vendors.textContent = 'Schedule your delivery for the next available slot';
    });
  }

  let observerTimer = 0;
  const observer = new MutationObserver((mutations) => {
    if (isRendering) return;

    const changedOutsideScheduler = mutations.some((mutation) => {
      const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
      return !target?.closest?.(PANEL_SELECTOR);
    });

    if (!changedOutsideScheduler) return;

    window.clearTimeout(observerTimer);
    observerTimer = window.setTimeout(evaluate, 100);
  });

  document.addEventListener('DOMContentLoaded', () => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) observer.observe(cartDrawer, { childList: true, subtree: true });
  });
})();
